import {
  useGetRevisionsQuery,
  useGetTourQuery,
  useUpdateTourDetailsMutation,
} from '@carboncopyco/data';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  InlineLink,
  LoadingDots,
  TextBase,
  TextCaption,
  TextLargeParagraph,
  TooltipBubble,
} from '@carboncopyco/ui';
import { dateUTCFromNow } from '@carboncopyco/utils';
import { Link } from 'react-router-dom';
import { usePermissionsContext } from '../../../utils';
import { TitleCard } from '../../InfoPageCards/TitleCard';
import { InfoPageEditableDescription } from '../../InfoPageEdiableDescription';
import { useTourPageProps } from '../TourPageContext';

export const TourDetailsCard = () => {
  const { organizationId, tourId, workspaceId, tourDemoPath, readOnly } =
    useTourPageProps();

  const { getPermissions } = usePermissionsContext();
  const { data: tour } = useGetTourQuery({
    organizationId,
    id: tourId,
    workspaceId,
  });

  const tourDemoData = tour?.demoData || [];
  const tourUrlData = tour?.urlData || [];

  const { data } = useGetRevisionsQuery(
    {
      organizationId,
      sourceType: 'tour',
      id: String(tourId),
      reverseOrder: true,
    },
    { skip: !tourId, refetchOnMountOrArgChange: true }
  );
  const [updateTourDetails] = useUpdateTourDetailsMutation();

  const revisions = data?.items;
  const latestVersion = revisions?.[0];
  const updateTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.updateTourById
  );
  return (
    <TitleCard
      header={
        <div className="flex justify-between items-center h-ds-15 pl-6">
          <TextLargeParagraph
            color="text-ds-natural-700"
            label="Details"
          />
        </div>
      }
      radius
    >
      {revisions && tourDemoPath ? (
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-1">
            <TextCaption label="Description" />
            <div className="max-w-ds-96">
              <InfoPageEditableDescription
                description={tour?.description || 'No description available'}
                disabled={readOnly || !updateTourPermission}
                onDescriptionChange={(value) =>
                  value
                    ? updateTourDetails({
                        organizationId,
                        id: tourId,
                        description: value,
                      })
                    : undefined
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <TextCaption label="Based on" />
            <div className="hover:text-ds-primary flex flex-col items-start">
              {tourDemoData?.map((demoData, index) => {
                const demoPath = `/${organizationId}/workspace/${demoData.workspaceId}/demo/${demoData.demoId}/overview`;
                return (
                  <div
                    className="inline-block"
                    key={demoData?.demoId?.toString() || `demoIndex-${index}`}
                  >
                    <TooltipBubble
                      absolute
                      placement="right-end"
                      title={demoData.demoName || ''}
                      disabled={!demoPath || !demoData.demoId}
                    >
                      <div className="flex items-center gap-1 truncate">
                        <TextBase
                          color="text-ds-natural-600"
                          label={`Demo ${index + 1}:`}
                        />
                        {demoData.demoName ? (
                          <InlineLink
                            disabled={tour?.readOnly}
                            as={Link}
                            to={demoPath}
                            label={`${demoData.demoName || ''}`}
                            truncateWidth={340}
                          />
                        ) : (
                          <TextBase
                            color="text-ds-natural-600"
                            label="Imported"
                          />
                        )}
                      </div>
                    </TooltipBubble>
                  </div>
                );
              })}
              {tourUrlData?.map((urlData, index) => {
                return (
                  <div
                    className="inline-block"
                    key={`urlIndex-${index}`}
                  >
                    <TooltipBubble
                      placement="right"
                      title={urlData.url || ''}
                      disabled={!urlData.url}
                    >
                      <div className="flex items-center gap-1 truncate">
                        <TextBase
                          color="text-ds-natural-600"
                          label={`URL ${index + 1}:`}
                        />
                        <InlineLink
                          href={urlData.url}
                          target="_blank"
                          label={urlData.url}
                          truncateWidth={340}
                        />
                      </div>
                    </TooltipBubble>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <TextCaption label="Version" />
            {latestVersion && (
              <div className="flex items-center gap-1">
                <TextBase
                  color="text-ds-natural-600"
                  label={`Version ${latestVersion.currRevision + 1}`}
                />
                <TextCaption
                  label={`(Edited ${dateUTCFromNow(
                    latestVersion.createTime
                  )}, by ${latestVersion.creator.name})`}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-[233px]">
          <LoadingDots />
        </div>
      )}
    </TitleCard>
  );
};
